// askSmart-ui/src/pages/MapleCoin/useMapleCoinAccount.ts
//
// The single seam between the dashboard and the MapleCoin banking backend.
// Everything shown comes from the read-only customer API: balances from the
// ledger, activity from the transaction layer. Nothing here calculates or
// stores a balance, and there is no sample data.
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAccount, fetchActivity, fetchMe } from './customerApi';
import { AccountActivity, MapleCoinAccountState } from './types';

export const PAGE_SIZE = 20;

export function useMapleCoinAccount() {
  const [state, setState] = useState<MapleCoinAccountState>({ status: 'loading' });
  const alive = useRef(true);
  // The latest state, readable synchronously (state updaters run later).
  const latest = useRef(state);
  latest.current = state;

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const me = await fetchMe();
      if (!me.onboarded) {
        if (alive.current) setState({ status: 'not_onboarded' });
        return;
      }
      const accounts = await Promise.all(me.accounts.map((a) => fetchAccount(a.accountNumber)));
      const pages = await Promise.all(accounts.map((a) => fetchActivity(a.accountNumber, { limit: PAGE_SIZE })));
      const activity: Record<string, AccountActivity> = {};
      pages.forEach((p) => {
        activity[p.accountNumber] = { transactions: p.transactions, nextBefore: p.nextBefore, loadingMore: false };
      });
      if (alive.current) setState({ status: 'ready', customer: me.customer, accounts, activity });
    } catch {
      // Never surface server or database detail to the customer.
      if (alive.current) setState({ status: 'error' });
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  /** Fetches the next (older) page of an account's activity. */
  const loadMore = useCallback(async (accountNumber: string) => {
    const snapshot = latest.current;
    if (snapshot.status !== 'ready') return;
    const cursor = snapshot.activity[accountNumber]?.nextBefore;
    if (!cursor || snapshot.activity[accountNumber].loadingMore) return;
    setState((s) =>
      s.status === 'ready'
        ? { ...s, activity: { ...s.activity, [accountNumber]: { ...s.activity[accountNumber], loadingMore: true } } }
        : s,
    );
    try {
      const page = await fetchActivity(accountNumber, { limit: PAGE_SIZE, before: cursor });
      if (!alive.current) return;
      setState((s) => {
        if (s.status !== 'ready') return s;
        const current = s.activity[accountNumber];
        return {
          ...s,
          activity: {
            ...s.activity,
            [accountNumber]: {
              transactions: [...current.transactions, ...page.transactions],
              nextBefore: page.nextBefore,
              loadingMore: false,
            },
          },
        };
      });
    } catch {
      if (!alive.current) return;
      setState((s) =>
        s.status === 'ready'
          ? { ...s, activity: { ...s.activity, [accountNumber]: { ...s.activity[accountNumber], loadingMore: false } } }
          : s,
      );
    }
  }, []);

  return { state, reload: load, loadMore };
}
